export type ValueExpression =
  | boolean
  | number
  | string
  | string[]
  | number[]
  | null
  | undefined;

export type Expression = [string, ...ValueExpression[]] | ValueExpression;

export const boolean = (value: ValueExpression): Expression => [
  "boolean",
  value,
  false,
];

export const getProperty = (fieldName: string): string[] => ["get", fieldName];

export const largerThan = (
  value: ValueExpression,
  value1: ValueExpression,
): Expression => [">", value, value1];

export const equals = (
  value: ValueExpression,
  value1: ValueExpression,
): Expression => ["==", value, value1];

export const not = (expression: Expression): Expression => {
  // @ts-expect-error have to skip types check here
  expression[0] = "!=";
  return expression;
};

export const any = (expressions: Expression[]): Expression => [
  "any",
  ...(expressions as ValueExpression[]),
];
export const anyEquals = (
  value: ValueExpression,
  comparators: ValueExpression[],
): Expression => {
  const conditions = comparators.map((value2) => equals(value, value2));
  return ["any", ...conditions] as ValueExpression;
};

export const all = (expressions: Expression[]): Expression => [
  "all",
  ...(expressions as ValueExpression[]),
];

interface Condition {
  condition: Expression;
  then: Expression;
}

export const switchCase = (
  branches: Condition[],
  fallback: Expression,
): Expression => {
  const branchConditions = branches
    .map(({ condition, then }) => [condition, then])
    .flat() as ValueExpression[];

  return ["case", ...branchConditions, fallback as ValueExpression];
};

export const ifThen = (
  condition: Expression,
  value: ValueExpression,
  orElse: ValueExpression,
): Expression => ["case", condition as ValueExpression, value, orElse];

export type FeatureState = "selected" | "hover" | "hidden" | "changed";

export const featureState = (property: FeatureState): ValueExpression => [
  "feature-state",
  property,
];

/**
 * CSS color name to number value
 * @param color
 */
export const toColor = (color: string): ValueExpression => ["to-color", color];

export interface InterpolationOptions {
  comparator: number;
  value: ValueExpression;
}

/**
 * Interpolate between two values based in a given property
 */
export const interpolate = (
  property: string[],
  { from, to }: { from: InterpolationOptions; to: InterpolationOptions },
  interpolateFunction: ValueExpression = ["linear"],
): Expression => [
  "interpolate",
  interpolateFunction,
  property,
  from.comparator,
  from.value,
  to.comparator,
  to.value,
];

export const expression = (expression: Expression) => expression as never;
